import { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery as useApolloQuery, useQuery } from '@apollo/client';
import { IGetCarQuery } from '../../services/carService/__generated__/queries.generated';
import { Modal } from '../../components/modal';
import { URLS } from '../../../constants/urls';
import { GET_CAR } from '../../services/carService/queries';
import ExtraItemsComponent from '../../components/ExtraItems';
import DealerInfo from '../../components/dealer/dealerInfo';
import CarsOnSale from '../../components/dealer/cars/carsOnSale';
import { GET_MY_DEALER } from '../../services/dealerService/queries';
import { GET_SPECIFIC_DEALER_AGREEMENTS } from '../../services/carSaleServices/queries';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/button';
import Dealerships from '../../../assets/images/dealership.webp';
import { toast } from 'react-toastify';
import _ from 'lodash';
import LoadingSpinner from '../../components/LoadingSpinner';
// import { updateCar } from '../../services/carService/mutations';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 box-border container mx-auto p-6 pb-8 md:pb-16`}
  ${tw`lg:flex lg:gap-8`}
`;

// Left Column
const LeftColumn = styled.div`
  ${tw`w-full lg:w-1/3`}
`;

const CarPreview = styled.div`
  ${tw`mb-6`}
`;

const CarImage = styled.div`
  ${tw`w-full rounded-lg mb-4 relative box-border`}
  img {
    ${tw`h-48 object-cover`}
  }
`;

const ViewListingButton = styled.button`
  ${tw`
    absolute bottom-0 left-0 
    w-full 
    text-black font-semibold 
    py-2 px-4 
    bg-gray-100/60
    border-none
  `}

  &:hover {
    ${tw`bg-gray-50/80 cursor-pointer`}
  }
`;

const DealerInfoContainer = styled.div`
  ${tw`mb-6`}
`;

const DealerNameSection = styled.h2`
  ${tw`text-xl font-bold mb-1`}
`;

const CarDetails = styled.p`
  ${tw`text-gray-600 text-sm`}
`;

const NavigationList = styled.nav`
  ${tw`space-y-2`}
`;

const NavItem = styled.a<{ isActive?: boolean }>`
  ${tw`block py-2 px-4 rounded-lg cursor-pointer flex justify-between items-center max-w-full min-w-0 box-border`}
  ${({ isActive }) => isActive && tw`bg-purple-100 text-purple-700`}
  &:hover {
    ${({ isActive }) => !isActive && tw`bg-gray-50`}
  }
`;

const UpdatedBadge = styled.span`
  ${tw`ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full`}
`;

// Right Column
const RightColumn = styled.div`
  ${tw`hidden lg:block lg:w-2/3`}
`;

export default function DealerPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('dealerInfo');

  const {
    data: dealerData,
    loading: dealerLoading,
    error: dealerError,
  } = useQuery(GET_MY_DEALER, {
    fetchPolicy: 'cache-first',
  });

  const handleNavClick = (section: string) => {
    setActiveSection(section);
    if (window.innerWidth < 1024) {
      setIsModalOpen(true);
    }
  };

  const renderRightColumnContent = () => {
    switch (activeSection) {
      case 'dealerInfo':
        return <DealerInfo />;

      case 'carsOnSale':
        return <CarsOnSale />;

      case 'agreements':
        return <AgreementList dealerId={dealerData?.myDealer?.id || ''} />;

      default:
        return <div>todo</div>;
    }
  };

  const onDealerInfoClick = () => {
    setActiveSection('dealerInfo');
  };

  const onCarsOnSaleClick = () => {
    if (!dealerData?.myDealer?.name) {
      toast.error('please add your dealer info first');
      return;
    }
    setActiveSection('carsOnSale');
  };

  const onAgreementsClick = () => {
    if (!dealerData?.myDealer?.name) {
      toast.error('please add your dealer info first');
      return;
    }
    setActiveSection('agreements');
  };

  const navigate = useNavigate();

  if (dealerLoading) return <LoadingSpinner fullScreen color="#3B82F6" />;

  return (
    <PageContainer>
      <LeftColumn>
        <CarPreview>
          <CarImage>
            <img src={Dealerships} width="100%" height="auto" />
            <ViewListingButton>View listing</ViewListingButton>
          </CarImage>

          {!_.isEmpty(dealerData?.myDealer) && (
            <DealerInfoContainer>
              <DealerNameSection>{dealerData?.myDealer?.name}</DealerNameSection>
              <CarDetails>
                {dealerData?.myDealer?.streetAddress}
                {dealerData?.myDealer?.city}, {dealerData?.myDealer?.state}{' '}
                {dealerData?.myDealer?.zipCode}
              </CarDetails>
              <CarDetails>{dealerData?.myDealer?.phone}</CarDetails>
            </DealerInfoContainer>
          )}
        </CarPreview>

        <NavigationList>
          <NavItem isActive={activeSection === 'dealerInfo'} onClick={onDealerInfoClick}>
            <span>dealer info</span>
          </NavItem>
          <NavItem onClick={onCarsOnSaleClick}>
            <span>cars on sale</span>
          </NavItem>
          <NavItem onClick={onAgreementsClick}>Agreements</NavItem>
        </NavigationList>
      </LeftColumn>

      <RightColumn>{renderRightColumnContent()}</RightColumn>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {renderRightColumnContent()}
      </Modal>
    </PageContainer>
  );
}

const ListContainer = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const CardContainer = styled.div`
  ${tw`flex flex-col gap-4 border border-blue-400 rounded-lg p-4 border-solid`}
`;

const CardHeader = styled.div`
  ${tw`flex justify-between items-start mb-2`}
`;

const CardInfo = styled.div`
  ${tw`flex-1`}
`;

const CardActions = styled.div`
  ${tw`flex gap-2`}
`;

const GenerateGuideButton = styled.button`
  ${tw`
    px-3 py-1 
    bg-green-500 text-white 
    rounded 
    transition-colors 
    duration-200
    cursor-pointer
    border-none
    font-medium
    text-sm
 max-w-full min-w-0 box-border`}

  &:hover {
    ${tw`bg-green-600`}
  }
`;

function AgreementList({ dealerId }: { dealerId: string }) {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<any>(GET_SPECIFIC_DEALER_AGREEMENTS, {
    variables: { dealerId },
  });

  const handleGenerateBuyersGuide = (agreement: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const urlParams = new URLSearchParams();
    urlParams.set('agreementId', agreement.id);
    urlParams.set('year', agreement.car.year);
    urlParams.set('make', agreement.car.make);
    urlParams.set('model', agreement.car.model);
    urlParams.set('vin', agreement.car.vin);
    navigate(`/dealer/buyers-guide?${urlParams.toString()}`);
  };

  if (loading) {
    return <LoadingSpinner fullScreen color="#3B82F6" />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <ListContainer>
      {data?.dealerCarSales.length > 0 ? (
        data?.dealerCarSales.map((agreement: any) => {
          if (!agreement.id || !agreement.buyer || !agreement.car || !agreement.car.make) {
            return null;
          }
          return (
            <CardContainer
              key={agreement.id}
              onClick={() => {
                const setUrlParams = new URLSearchParams();
                setUrlParams.set('agreementId', agreement.id);
                navigate(`/dealer/agreement?${setUrlParams.toString()}`);
              }}
            >
              <CardHeader>
                <CardInfo>
                  <div>{agreement.id}</div>
                  <div>buyer: {agreement.buyer[0].name}</div>
                  <div>
                    make-model-year: {agreement.car.make} {agreement.car.model} {agreement.car.year}
                  </div>
                  <div>VIN: {agreement.car.vin}</div>
                  <div>Created On: {new Date(agreement.createdAt).toLocaleDateString()}</div>
                </CardInfo>
                <CardActions>
                  <GenerateGuideButton onClick={e => handleGenerateBuyersGuide(agreement, e)}>
                    Generate Buyer's Guide
                  </GenerateGuideButton>
                </CardActions>
              </CardHeader>
            </CardContainer>
          );
        })
      ) : (
        <div>No agreements found</div>
      )}

      <Button
        text="Create New Agreement"
        onClick={() => navigate('/dealer/agreement')}
        buttonWidth="200px"
        buttonHeight="40px"
      />
    </ListContainer>
  );
}
